import { useEffect, useMemo } from "react";

import { compact, flatten } from "lodash";

import { useUser } from "@components/user-context";
import { QueryParamAttributionWindowToAttrModelMapping } from "@features/benchmarks/components/benchmark-setting-section";
import {
  useBenchmarkAttributionWindow,
  useGetBenchmarkPlatformsToUse,
  useOrderedData,
} from "@features/benchmarks/hooks";
import {
  FETCH_BENCHMARK_SETTINGS,
  FETCH_PLATFORM_BENCHMARKS,
} from "@gql/benchmarks";
import { useNorthbeamLazyQuery } from "@hooks/http";
import {
  type FetchBenchmarkSettings,
  type FetchBenchmarkSettingsVariables,
  type FetchPlatformBenchmarks,
  type FetchPlatformBenchmarksVariables,
} from "@north-beam/nb-client-side";
import { BenchmarkPlatformEnum } from "@north-beam/nb-client-side";

export type SalesTableBenchmarks = Record<
  string,
  Record<
    string,
    {
      blended: number | null;
      firstTime: number | null;
      returning: number | null;
    }
  >
>;

const BenchmarkPlatformToPlatform = {
  [BenchmarkPlatformEnum.adwords]: "Google Ads",
  [BenchmarkPlatformEnum.facebook]: "Facebook Ads",
  [BenchmarkPlatformEnum.youtube]: "YouTube Ads",
};

export const useSalesTableBenchmarks = (attributionModel: string) => {
  const { user } = useUser();
  const benchmarkAttributionModel =
    QueryParamAttributionWindowToAttrModelMapping[attributionModel];
  const [benchmarkAttributionWindow] = useBenchmarkAttributionWindow();
  const platforms = useGetBenchmarkPlatformsToUse();

  const memoizedBenchmarkAttributionModels = useMemo(
    () => [benchmarkAttributionModel].filter(Boolean),
    [benchmarkAttributionModel],
  );

  const {
    loadData: loadBenchmarkSettings,
    data: { data: currentBenchmarkSettings, loading: benchmarkSettingsLoading },
  } = useNorthbeamLazyQuery<
    FetchBenchmarkSettings,
    FetchBenchmarkSettingsVariables
  >(FETCH_BENCHMARK_SETTINGS);

  useEffect(() => {
    if (
      user.featureFlags.showBenchmarks &&
      benchmarkAttributionWindow &&
      benchmarkAttributionModel
    ) {
      loadBenchmarkSettings({
        variables: {
          attributionWindow: benchmarkAttributionWindow,
          attributionModel: benchmarkAttributionModel,
        },
      });
    }
  }, [
    user,
    benchmarkAttributionWindow,
    benchmarkAttributionModel,
    loadBenchmarkSettings,
  ]);

  const {
    loadData: loadPlatformBenchmarks,
    data: { loading: platformBenchmarksLoading, data: platformBenchmarks },
  } = useNorthbeamLazyQuery<
    FetchPlatformBenchmarks,
    FetchPlatformBenchmarksVariables
  >(FETCH_PLATFORM_BENCHMARKS);

  useEffect(() => {
    if (
      currentBenchmarkSettings?.me.benchmarks.benchmarkSettings &&
      memoizedBenchmarkAttributionModels.length &&
      benchmarkAttributionWindow &&
      benchmarkAttributionModel
    ) {
      const benchmarkSettings =
        currentBenchmarkSettings.me.benchmarks.benchmarkSettings;

      const benchmarkTargetsInput: FetchPlatformBenchmarksVariables["benchmarkTargets"] =
        benchmarkSettings.map((setting) => ({
          attributionModels: memoizedBenchmarkAttributionModels,
          attributionWindow: setting.attributionWindow,
          benchmarkType: setting.benchmark,
          platform: setting.platform,
          target: setting.target,
        }));

      loadPlatformBenchmarks({
        variables: {
          benchmarkTargets: benchmarkTargetsInput,
        },
      });
    }
  }, [
    benchmarkAttributionWindow,
    benchmarkAttributionModel,
    memoizedBenchmarkAttributionModels,
    platforms,
    currentBenchmarkSettings,
    loadPlatformBenchmarks,
  ]);

  const orderedData = useOrderedData({
    platforms,
    attributionModels: memoizedBenchmarkAttributionModels,
    data: platformBenchmarks,
  });

  const customBenchmarks = useMemo(() => {
    const _benchmarks = compact(
      flatten(
        currentBenchmarkSettings?.me.benchmarks.benchmarkSettings?.map(
          (benchmarkSettings) => benchmarkSettings.customBenchmarks,
        ),
      ),
    );
    return _benchmarks.length ? _benchmarks : null;
  }, [currentBenchmarkSettings?.me.benchmarks.benchmarkSettings]);

  const salesTableBenchmarks = useMemo(() => {
    const data: SalesTableBenchmarks = {};

    if (!benchmarkAttributionWindow || !benchmarkAttributionModel) {
      return data;
    }

    for (const row of orderedData) {
      const platformCac = row.cac;
      const platformRoas = row.roas;

      let customCac;
      let customRoas;

      if (customBenchmarks) {
        const customBenchmark = customBenchmarks.find(
          (e) => e.platform === row.platform,
        );

        if (customBenchmark) {
          customCac = customBenchmark.cac;
          customRoas = customBenchmark.roas;
        }
      }

      const cac = customCac || platformCac;
      const roas = customRoas || platformRoas;

      if (!cac) {
        continue;
      }
      if (!roas) {
        continue;
      }

      const platform = BenchmarkPlatformToPlatform[row.platform];

      data[platform] = {
        cac: {
          blended: cac.blended,
          firstTime: cac.firstTime,
          returning: cac.returning,
        },
        roas: {
          blended: roas.blended,
          firstTime: roas.firstTime,
          returning: roas.returning,
        },
      };
    }

    return data;
  }, [
    benchmarkAttributionWindow,
    benchmarkAttributionModel,
    orderedData,
    customBenchmarks,
  ]);

  return {
    salesTableBenchmarks,
    loadingSalesTableBenchmarks:
      benchmarkSettingsLoading || platformBenchmarksLoading,
  };
};
